import React, { useState } from 'react';
import { setPageMeta } from '../utils/seo';
import { Mail, Phone, MapPin, Clock, MessageCircle, MessageSquare } from 'lucide-react';
import { createGeneralContact } from '../services/firebaseService';
import { sendContactEmail } from '../services/emailService';

const WHATSAPP_NUMBER = (process.env.REACT_APP_COMPANY_PHONE || '+351 911 504 560').replace(/\D/g, '');

const Contact = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    mensagem: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  React.useEffect(() => {
    setPageMeta({
      title: 'Contacto',
      description: 'Entre em contacto com a Diamond Cars em Ovar. Telefone, email e morada. Estamos disponíveis para o ajudar a encontrar a viatura ideal.',
      path: '/contacto'
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Enviar email
      const emailResult = await sendContactEmail(formData);

      // Criar lead no sistema
      const leadResult = await createGeneralContact(formData);

      if (emailResult.success || leadResult.success) {
        setSubmitSuccess(true);
        setFormData({ nome: '', email: '', telefone: '', mensagem: '' });
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        alert('Erro ao enviar mensagem. Por favor tente novamente.');
      }
    } catch (error) {
      console.error('Erro:', error);
      alert('Erro ao enviar mensagem. Por favor tente novamente.');
    }

    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Entre em Contacto
          </h1>
          <p className="text-xl text-gray-600">
            Estamos à disposição para ajudar. Fale connosco!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Informações de Contacto */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Informações de Contacto
              </h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <a
                      href={`mailto:${process.env.REACT_APP_COMPANY_EMAIL}`}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      {process.env.REACT_APP_COMPANY_EMAIL || 'diamond.cars.rg@gmail.com'}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Phone className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Telefone</h3>
                    <a
                      href={`tel:${process.env.REACT_APP_COMPANY_PHONE}`}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      {process.env.REACT_APP_COMPANY_PHONE || '+351 911 504 560'}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <MessageSquare className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">WhatsApp</h3>
                    <a
                      href={`https://wa.me/${WHATSAPP_NUMBER}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:text-green-700 font-medium"
                    >
                      {process.env.REACT_APP_COMPANY_PHONE || '+351 911 504 560'}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Morada</h3>
                    <p className="text-gray-600">
                      {process.env.REACT_APP_COMPANY_ADDRESS || 'R. Monsenhor Miguel de Oliveira 1272, 3880-796 Ovar'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Horário</h3>
                    <p className="text-gray-600">
                      Segunda a Sexta: 9h00 - 18h00
                      <br />
                      Sábado: 9h00 - 13h00
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Informação Extra */}
            <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold mb-4">
                Porque Escolher-nos?
              </h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <span className="text-blue-300">✓</span>
                  <span>Viaturas de qualidade certificada</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-300">✓</span>
                  <span>Garantia e suporte pós-venda</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-300">✓</span>
                  <span>Financiamento personalizado</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-300">✓</span>
                  <span>Equipa experiente e dedicada</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Formulário de Contacto */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Envie-nos uma Mensagem
            </h2>

            {submitSuccess && (
              <div className="mb-6 bg-green-50 border-2 border-green-200 text-green-800 px-4 py-3 rounded-lg">
                Mensagem enviada com sucesso! Entraremos em contacto em breve.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome *
                </label>
                <input
                  type="text"
                  required
                  value={formData.nome}
                  onChange={(e) =>
                    setFormData({ ...formData, nome: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="O seu nome"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="seuemail@exemplo.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Telefone *
                </label>
                <input
                  type="tel"
                  required
                  value={formData.telefone}
                  onChange={(e) =>
                    setFormData({ ...formData, telefone: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="+351 XXX XXX XXX"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Mensagem *
                </label>
                <textarea
                  required
                  rows="6"
                  value={formData.mensagem}
                  onChange={(e) =>
                    setFormData({ ...formData, mensagem: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-600 focus:border-transparent resize-none"
                  placeholder="Como podemos ajudar?"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-md transition font-semibold flex items-center justify-center gap-2 disabled:opacity-50 shadow-md hover:shadow-lg"
              >
                <MessageCircle className="w-5 h-5" />
                <span>{submitting ? 'A enviar...' : 'Enviar Mensagem'}</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
